import { HardwareIntegrationInput } from "@/types/system-setting/hardware-integration";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Control, Controller, FieldErrors, UseFormWatch } from "react-hook-form";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { useMemo } from "react";

interface HardwareDetailsSectionProp {
  control: Control<HardwareIntegrationInput>;
  errors: FieldErrors<HardwareIntegrationInput>;
  watch: UseFormWatch<HardwareIntegrationInput>;
  isDisableBooking?: boolean;
  setIsDisableBooking?: (value: boolean) => void;
  [key: string]: any;
}

const HardwareDetailsSection = ({
  control,
  errors,
  watch,
}: HardwareDetailsSectionProp) => {
  const watcher = watch();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const {
    data: facilitiesData,
    isLoading,
    refetch: refetchforcredits,
  } = useGetFacilitiesQuery({ orgId: orgId, query: "status=active&access_control=true" });
  
  const facilityOptions = useMemo(() => {
    return Array.isArray(facilitiesData?.data) ? facilitiesData?.data : [];
  }, [facilitiesData]);
  return (
    <div className="h-[30%] w-[95%] rounded-2xl bg-white">
      <CustomCollapsible
        title="Hardware Details"
        hideToggle={false}
        isOpenDefault={true}
      >
        <div className="flex flex-row gap-5 justify-start items-center">
          {/* Name Field */}
          <div className="w-[30%]">
            <p className="text-base font-normal">
              Name <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="name"
              rules={{
                required: "Name is required",
                maxLength: {
                  value: 40,
                  message: "Name cannot exceed 40 characters",
                },
                validate: (value) =>
                  value.trim() !== "" || "Name cannot be empty or only spaces",
              }}
              render={({ field }) => (
                <Input className="w-full" placeholder="Enter Name" {...field} />
              )}
            />
            {errors.name?.message && (
              <p className="text-red-500 text-xs mt-[5px] ">
                {errors.name.message.toString()}
              </p>
            )}
          </div>

          {/* Connection Key Field */}
          <div className="w-[45%]">
            <p className="text-base font-normal">
              Connection Key <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="connection_key"
              rules={{
                required: "Connection Key is required",
                maxLength: {
                  value: 100,
                  message: "Connection Key cannot exceed 100 characters",
                },
                validate: (value) =>
                  value.trim() !== "" || "Name cannot be empty or only spaces",
              }}
              render={({ field }) => (
                <div className="w-full gap-5 flex flex-row justify-center items-center">
                  <Input
                    className="w-full"
                    placeholder="Enter Connection Key"
                    {...field}
                  />
                </div>
              )}
            />

            {errors.connection_key?.message && (
              <p className=" text-red-500 text-xs mt-[5px]">
                {errors.connection_key.message.toString()}
              </p>
            )}
          </div>

          {watcher.use_facility && (
            <div className="w-[30%]">
              <Controller
                name="facility_id"
                rules={{ required: "Facility is required" }}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <>
                    <div>
                      <div>
                        <p className="text-base font-normal">
                          Facility <span className="text-red-500">*</span>
                        </p>
                      </div>
                      <Select
                        onValueChange={(value) => onChange(value)}
                        defaultValue={value ? value.toString() : undefined}
                        disabled={watcher.use_facility === false}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Facility" />
                        </SelectTrigger>
                        <SelectContent>
                          {facilityOptions?.map(
                            (facility) => (
                              <SelectItem
                                key={facility.id}
                                value={String(facility.id)}
                              >
                                {facility.name.length > 27
                                  ? `${facility.name.slice(0, 27)}...`
                                  : facility.name}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    {error && (
                      <span className="text-red-500 text-xs mt-[5px]">
                        {error.message}
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-row gap-8">
          <div className="w-[55%]">
            <div>
              <p className="text-base font-normal">Description</p>
            </div>
            <Controller
              control={control}
              name="description"
              rules={{
                maxLength: {
                  value: 200,
                  message: "Description cannot exceed 200 characters",
                },
              }}
              render={({ field }) => (
                <Textarea
                  placeholder="Type your Description here"
                  className="resize-none"
                  {...field}
                  value={field.value ?? ""}
                />
              )}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Controller
              control={control}
              name="enabled_booking"
              render={({ field }) => (
                <div className="flex flex-col gap-2 items-start w-full">
                  <span className="text-sm font-normal">
                    {field.value ? "Booking Enabled" : "Booking Disabled"}
                  </span>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default HardwareDetailsSection;


